@if (!isLoading) {
  <div class="form-finder-container">
    <!-- Header -->
    <div class="finder-header">
      <div class="finder-title-group">
        <button type="button" mat-icon-button matTooltip="Back to Settings" aria-label="Back" (click)="goBack()">
          <lucide-icon name="arrow-left" [size]="20"></lucide-icon>
        </button>
        <h2 class="finder-title">
          <lucide-icon name="file-text" [size]="20"></lucide-icon>
          Form Builder
        </h2>
      </div>
      <div class="header-actions">
        <button mat-icon-button
          matTooltip="Help & Tutorial"
          (click)="openTutorial()">
          <lucide-icon name="circle-help" [size]="20"></lucide-icon>
        </button>
        <button mat-raised-button
          [color]="isMultiSelectMode ? 'accent' : 'primary'"
          (click)="toggleMultiSelectMode()">
          <mat-icon>{{ isMultiSelectMode ? 'close' : 'checklist' }}</mat-icon>
          {{ isMultiSelectMode ? 'Cancel Select' : 'Print to PDF' }}
        </button>
        @if (isMultiSelectMode) {
          <button mat-raised-button color="primary"
            [disabled]="selectedFormsForPrint.size === 0"
            (click)="openPrintPreview()">
            <lucide-icon name="file-text" [size]="20"></lucide-icon>
            Print to PDF ({{ selectedFormsForPrint.size }})
          </button>
        }
        @if (!isMultiSelectMode) {
          <button mat-raised-button color="primary" (click)="createNewFormButtonPressed()">
            <lucide-icon name="plus" [size]="20"></lucide-icon>
            New Form
          </button>
        }
      </div>
    </div>
    <div class="finder-content">
      <!-- Left Panel: Search and Form List -->
      <div class="search-panel">
        <div class="search-section">
          <h3 class="section-title">Search Forms</h3>
          <mat-form-field class="search-field" appearance="outline">
            <lucide-icon name="search" [size]="20" matPrefix></lucide-icon>
            <input matInput
              [(ngModel)]="formSearchKeyword"
              (input)="searchForForm()">
              @if (formSearchKeyword) {
                <button mat-icon-button matSuffix (click)="clearSearch()">
                  <lucide-icon name="x" [size]="20"></lucide-icon>
                </button>
              }
            </mat-form-field>
            @if (isMultiSelectMode) {
              <div class="select-actions">
                <button mat-button (click)="selectAllForms()">Select All</button>
                <button mat-button (click)="deselectAllForms()">Deselect All</button>
                <span class="select-count">{{ selectedFormsForPrint.size }} selected</span>
              </div>
            }
            <!-- View Toggle: All Forms / Recently Deleted -->
            @if (!isMultiSelectMode) {
              <div class="list-view-toggle">
                <button class="toggle-btn"
                  [class.active]="!isTrashView"
                  (click)="switchToAllForms()">
                  <lucide-icon name="file-text" [size]="20"></lucide-icon>
                  All Forms
                </button>
                <button class="toggle-btn"
                  [class.active]="isTrashView"
                  (click)="switchToTrashView()">
                  <lucide-icon name="trash-2" [size]="20"></lucide-icon>
                  Recently Deleted
                  @if (trashEntries.length > 0) {
                    <span class="trash-count">({{ trashEntries.length }})</span>
                  }
                </button>
              </div>
            }
            @if (!isTrashView && formSearchResults !== null) {
              <div class="search-results">
                @if (isSearchingForForms) {
                  <div class="search-spinner">
                    <mat-spinner [diameter]="24"></mat-spinner>
                  </div>
                }
                @if (!isSearchingForForms && formSearchResults.length === 0) {
                  <div class="no-results">
                    No forms found
                  </div>
                }
                @if (!isSearchingForForms && formSearchResults.length > 0) {
                  <div class="form-list">
                    @for (form of formSearchResults; track form) {
                      <div class="form-list-item"
                        [class.selected]="!isMultiSelectMode && form.DFormId === selectedCfgFormPreviewId"
                        [class.checked]="isMultiSelectMode && isFormSelected(form.DFormId)"
                        (click)="isMultiSelectMode ? toggleFormSelection(form.DFormId) : selectAndFetchCfgForm(form.DFormId)">
                        @if (isMultiSelectMode) {
                          <mat-checkbox
                            [checked]="isFormSelected(form.DFormId)"
                            (click)="$event.stopPropagation()"
                            (change)="toggleFormSelection(form.DFormId)">
                          </mat-checkbox>
                        }
                        <lucide-icon name="file-text" [size]="20" class="form-icon"></lucide-icon>
                        <span class="form-name">{{ form.DFormName }}</span>
                      </div>
                    }
                  </div>
                }
              </div>
            }
            @if (!isTrashView && formSearchResults === null && !cfgFormPreviewSelectListIsLoading) {
              <div class="recent-forms">
                <h4 class="subsection-title">All Forms</h4>
                <div class="form-list">
                  @for (form of cfgFormPreviewSelectList; track form) {
                    <div class="form-list-item"
                      [class.selected]="!isMultiSelectMode && form.DFormId === selectedCfgFormPreviewId"
                      [class.checked]="isMultiSelectMode && isFormSelected(form.DFormId)"
                      (click)="isMultiSelectMode ? toggleFormSelection(form.DFormId) : selectAndFetchCfgForm(form.DFormId)">
                      @if (isMultiSelectMode) {
                        <mat-checkbox
                          [checked]="isFormSelected(form.DFormId)"
                          (click)="$event.stopPropagation()"
                          (change)="toggleFormSelection(form.DFormId)">
                        </mat-checkbox>
                      }
                      <lucide-icon name="file-text" [size]="20" class="form-icon"></lucide-icon>
                      <span class="form-name">{{ form.DFormName }}</span>
                    </div>
                  }
                </div>
              </div>
            }
            <!-- Trash View -->
            @if (isTrashView && formSearchResults === null && !cfgFormPreviewSelectListIsLoading) {
              <div class="trash-forms">
                <div class="trash-list" data-cy="form-finder-trash-list">
                  @if (trashEntries.length === 0) {
                    <div class="no-results">
                      No recently deleted items
                    </div>
                  }
                  <!-- Deleted Forms -->
                  @if (deletedFormEntries.length > 0) {
                    <h4 class="subsection-title">Deleted Forms</h4>
                    <div class="form-list">
                      @for (entry of deletedFormEntries; track entry) {
                        <div class="form-list-item trash-item"
                          [class.selected]="selectedTrashEntry?.id === entry.id"
                          (click)="selectTrashEntry(entry)">
                          <lucide-icon name="trash-2" [size]="20" class="form-icon trash-icon"></lucide-icon>
                          <div class="trash-item-info">
                            <span class="form-name">{{ entry.name }}</span>
                            <span class="trash-date">Deleted {{ entry.deletedOn | date:'shortDate' }}</span>
                          </div>
                          <button mat-icon-button
                            matTooltip="Restore form"
                            (click)="restoreForm(entry); $event.stopPropagation()">
                            <lucide-icon name="rotate-ccw" [size]="20"></lucide-icon>
                          </button>
                        </div>
                      }
                    </div>
                  }
                  <!-- Deleted Versions (grouped by parent form) -->
                  @if (deletedVersionsGrouped.length > 0) {
                    <h4 class="subsection-title">Deleted Versions</h4>
                    @for (group of deletedVersionsGrouped; track group) {
                      <div>
                        <div class="trash-group-header">{{ group.formName }}</div>
                        <div class="form-list">
                          @for (entry of group.versions; track entry) {
                            <div class="form-list-item trash-item"
                              [class.selected]="selectedTrashEntry?.id === entry.id"
                              (click)="selectTrashEntry(entry)">
                              <lucide-icon name="history" [size]="20" class="form-icon trash-icon"></lucide-icon>
                              <div class="trash-item-info">
                                <span class="form-name">{{ entry.name }}</span>
                                <span class="trash-date">Deleted {{ entry.deletedOn | date:'shortDate' }}</span>
                              </div>
                              <button mat-icon-button
                                matTooltip="Restore version"
                                (click)="restoreVersion(entry); $event.stopPropagation()">
                                <lucide-icon name="rotate-ccw" [size]="20"></lucide-icon>
                              </button>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  }
                </div>
                <!-- Clear Recently Deleted -->
                @if (trashEntries.length > 0) {
                  <div class="trash-actions">
                    <button mat-raised-button color="warn" class="clear-trash-btn" (click)="clearAllTrash()"
                      matTooltip="Permanently remove all recently deleted items">
                      <lucide-icon name="trash-2" [size]="20"></lucide-icon>
                      Clear Recently Deleted
                    </button>
                  </div>
                }
              </div>
            }
            @if (cfgFormPreviewSelectListIsLoading) {
              <div class="loading-forms">
                <mat-spinner [diameter]="24"></mat-spinner>
                <span>Loading forms...</span>
              </div>
            }
          </div>
        </div>
        <!-- Right Panel: Form Details & Versions -->
        <div class="details-panel">
          <!-- Loading State -->
          @if (cfgFormIsLoading) {
            <div class="loading-state">
              <mat-spinner [diameter]="40"></mat-spinner>
              <span>Loading form details...</span>
            </div>
          }
          <!-- Empty State -->
          @if (!selectedCfgForm && !selectedTrashEntry && !cfgFormIsLoading) {
            <div class="empty-state">
              <lucide-icon name="pointer" [size]="20"></lucide-icon>
              <h3>Select a Form</h3>
              <p>Choose a form from the list to view its versions and details, or create a new form to get started.</p>
            </div>
          }
          <!-- Trash Item Detail -->
          @if (selectedTrashEntry && !cfgFormIsLoading) {
            <div class="empty-state trash-detail">
              <lucide-icon name="trash-2" [size]="20"></lucide-icon>
              <h3>{{ selectedTrashEntry.name }}</h3>
              @if (selectedTrashEntry.type === 'form') {
                <p>
                  This form was deleted on {{ selectedTrashEntry.deletedOn | date:'medium' }}.
                  It will be automatically removed from this list after 30 days.
                </p>
              }
              @if (selectedTrashEntry.type === 'version') {
                <p>
                  This version of "{{ selectedTrashEntry.parentFormName }}" was deleted on {{ selectedTrashEntry.deletedOn | date:'medium' }}.
                  It will be automatically removed from this list after 30 days.
                </p>
              }
              <button mat-raised-button color="primary" (click)="restoreSelectedTrashEntry()">
                <lucide-icon name="rotate-ccw" [size]="20"></lucide-icon>
                Restore
              </button>
            </div>
          }
          <!-- Form Details -->
          @if (selectedCfgForm && !cfgFormIsLoading) {
            <div class="form-details">
              <!-- Form Header -->
              <div class="form-header">
                <div class="form-title-section">
                  <h3 class="form-name">{{ selectedCfgForm.DFormName }}</h3>
                  <div class="form-meta">
                    @if (getCategoryValue(selectedCfgForm.enumFormCategoryID)) {
                      <span class="meta-item">
                        <lucide-icon name="tag" [size]="20"></lucide-icon>
                        {{ getCategoryValue(selectedCfgForm.enumFormCategoryID)?.ItemDisplayValue }}
                      </span>
                    }
                    @if (getDataSourceValue(selectedCfgForm.enumFormDataSourceID)) {
                      <span class="meta-item">
                        <lucide-icon name="code" [size]="20"></lucide-icon>
                        {{ getDataSourceValue(selectedCfgForm.enumFormDataSourceID)?.name }}
                      </span>
                    }
                  </div>
                </div>
                <div class="form-actions">
                  <button mat-icon-button matTooltip="Delete form" (click)="deleteForm()">
                    <lucide-icon name="trash-2" [size]="20"></lucide-icon>
                  </button>
                </div>
              </div>
              <!-- Form Properties -->
              <mat-expansion-panel class="properties-panel" #propertiesPanel>
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    <lucide-icon name="settings" [size]="20"></lucide-icon>
                    Form Properties
                  </mat-panel-title>
                </mat-expansion-panel-header>
                <div class="properties-content">
                  <div class="property-row">
                    <div class="property-label-row">
                      <label>Form Name</label>
                      <lucide-icon name="circle-help" [size]="14" class="property-help"
                        [matTooltip]="helpText.formName" matTooltipClass="property-help-tooltip"
                        data-cy="form-finder-help-form-name"></lucide-icon>
                    </div>
                    <input matInput [(ngModel)]="selectedCfgForm.DFormName" class="property-input">
                  </div>
                  <div class="property-row">
                    <div class="property-label-row">
                      <label>Category</label>
                      <lucide-icon name="circle-help" [size]="14" class="property-help"
                        [matTooltip]="helpText.category" matTooltipClass="property-help-tooltip"
                        data-cy="form-finder-help-category"></lucide-icon>
                    </div>
                    <tdo-dropdown-v2
                      placeholder="Select category"
                      displayProperty="ItemDisplayValue"
                      [datasource]="categoryEnums"
                      [value]="getCategoryValue(selectedCfgForm.enumFormCategoryID)"
                      (selectionChange)="selectedCfgForm.enumFormCategoryID = $event.value.ValidValueItemId;">
                    </tdo-dropdown-v2>
                  </div>
                  <div class="property-row">
                    <div class="property-label-row">
                      <label>Data Source</label>
                      <lucide-icon name="circle-help" [size]="14" class="property-help"
                        [matTooltip]="helpText.dataSource" matTooltipClass="property-help-tooltip"
                        data-cy="form-finder-help-data-source"></lucide-icon>
                    </div>
                    <tdo-dropdown-v2
                      placeholder="Select data source"
                      displayProperty="name"
                      [datasource]="cfgFormDataSourceList"
                      [value]="getDataSourceValue(selectedCfgForm.enumFormDataSourceID)"
                      (selectionChange)="selectedCfgForm.enumFormDataSourceID = $event.value.id;">
                    </tdo-dropdown-v2>
                  </div>
                  @if (selectedCfgForm?.enumFormDataSourceID === globals.FORM_DATA_SOURCE_CUSTOM) {
                    <div class="property-row">
                      <div class="property-label-row">
                        <label>Type</label>
                        <lucide-icon name="circle-help" [size]="14" class="property-help"
                          [matTooltip]="helpText.customType" matTooltipClass="property-help-tooltip"
                          data-cy="form-finder-help-custom-type"></lucide-icon>
                      </div>
                      <tdo-dropdown-v2
                        placeholder="Select type"
                        displayProperty="ItemDisplayValue"
                        [datasource]="customEnums"
                        [value]="getCustomValue(selectedCfgForm.enumCustomFormID)"
                        (selectionChange)="selectedCfgForm.enumCustomFormID = $event.value.ValidValueItemId;">
                      </tdo-dropdown-v2>
                    </div>
                  }
                  <div class="property-checkboxes">
                    <div class="property-checkbox-row">
                      <mat-checkbox [(ngModel)]="selectedCfgForm.isRequireSignature">
                        Flag as needing signature
                      </mat-checkbox>
                      <lucide-icon name="circle-help" [size]="14" class="property-help"
                        [matTooltip]="helpText.requireSignature" matTooltipClass="property-help-tooltip"
                        data-cy="form-finder-help-require-signature"></lucide-icon>
                    </div>
                    <div class="property-checkbox-row">
                      <mat-checkbox [(ngModel)]="selectedCfgForm.isToothRequired">
                        Require tooth selection
                      </mat-checkbox>
                      <lucide-icon name="circle-help" [size]="14" class="property-help"
                        [matTooltip]="helpText.requireTooth" matTooltipClass="property-help-tooltip"
                        data-cy="form-finder-help-require-tooth"></lucide-icon>
                    </div>
                    <div class="property-checkbox-row">
                      <mat-checkbox [(ngModel)]="selectedCfgForm.isSubmissionRequired">
                        Show "Complete Form" button
                      </mat-checkbox>
                      <lucide-icon name="circle-help" [size]="14" class="property-help"
                        [matTooltip]="helpText.requireSubmission" matTooltipClass="property-help-tooltip"
                        data-cy="form-finder-help-require-submission"></lucide-icon>
                    </div>
                  </div>
                  <div class="property-actions">
                    @if (!canSaveFormProperties) {
                      <span class="property-save-hint">{{ savePropertiesTooltip }}</span>
                    }
                    <button mat-raised-button color="primary"
                      (click)="saveFormProperties()"
                      [disabled]="!canSaveFormProperties"
                      [matTooltip]="savePropertiesTooltip">
                      <lucide-icon name="save" [size]="20"></lucide-icon>
                      Save Properties
                    </button>
                  </div>
                </div>
              </mat-expansion-panel>
              <!-- Versions Section -->
              @if (isSelectingActiveVersion) {
                <div class="set-active-overlay" (click)="cancelVersionSelection()"></div>
              }
              <div class="versions-section" [class.in-selection-mode]="isSelectingActiveVersion">
                <div class="versions-header">
                  <h4>
                    <lucide-icon name="history" [size]="20"></lucide-icon>
                    Versions ({{ selectedCfgForm.cfgDFormVersions?.length || 0 }})
                  </h4>
                  @if (selectedCfgForm?.enumFormDataSourceID !== globals.FORM_DATA_SOURCE_CUSTOM) {
                    <div style="display:flex;align-items:center;gap:8px;">
                      <button mat-raised-button
                        [color]="isSelectingActiveVersion ? 'warn' : 'primary'"
                        [disabled]="!selectedCfgForm.cfgDFormVersions?.length && !isSelectingActiveVersion"
                        (click)="isSelectingActiveVersion ? cancelVersionSelection() : enterVersionSelectionMode()">
                        <lucide-icon [name]="isSelectingActiveVersion ? 'x' : 'check-circle'" [size]="20"></lucide-icon>
                        {{ isSelectingActiveVersion ? 'Cancel' : 'Set Active Version' }}
                      </button>

                      <!-- Intentionally not [disabled] when the form is unsaved: a disabled button
                           swallows the click, so the handler can never explain what to do. The
                           handlers guard on formIsSaved and open Form Properties instead. -->
                      <button mat-raised-button color="primary"
                        (click)="createNewVersionForCurrentForm()"
                        [matTooltip]="unsavedFormTooltip">
                        <lucide-icon name="plus" [size]="20"></lucide-icon>
                        New Version
                      </button>

                      <button mat-raised-button color="primary"
                        (click)="importPdfAsNewVersion()"
                        [matTooltip]="unsavedFormTooltip || 'Import a PDF or Word document into a new version created for you automatically'">
                        <lucide-icon name="file-up" [size]="20"></lucide-icon>
                        Import Document
                      </button>
                    </div>
                  }
                </div>
                @if (selectedCfgForm?.enumFormDataSourceID === globals.FORM_DATA_SOURCE_CUSTOM) {
                  <div class="no-versions">
                    <lucide-icon name="wrench" [size]="20"></lucide-icon>
                    <p>This is a custom form. It uses a built-in renderer and does not support versions.</p>
                  </div>
                }
                @if (selectedCfgForm.cfgDFormVersions?.length > 0 && selectedCfgForm?.enumFormDataSourceID !== globals.FORM_DATA_SOURCE_CUSTOM) {
                  <div class="versions-table-container">
                    <table class="versions-table">
                      <thead>
                        <tr>
                          <th>Version</th>
                          <th>Status</th>
                          <th>Last Edited</th>
                          <th>Paragraphs</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (version of selectedCfgForm.cfgDFormVersions; track version) {
                          <tr
                            class="version-row"
                            [class.selectable]="isSelectingActiveVersion"
                            (click)="isSelectingActiveVersion ? handleVersionSelectionClick(version) : editVersion(version)">
                            <td class="version-number">
                              <strong>v{{ version.Version }}.{{ version.Revision }}</strong>
                            </td>
                            <td class="version-status">
                              <span class="status-badge" [ngClass]="'status-' + getVersionStatusClass(version.enumDFormBuildStatusID, version)">
                                {{ getVersionStatusName(version.enumDFormBuildStatusID, version) }}
                              </span>
                            </td>
                            <td class="version-last-edited">
                              @if (getVersionLastEdited(version); as lastEdited) {
                                {{ lastEdited | date:'short' }}
                              } @else {
                                —
                              }
                            </td>
                            <td class="version-paragraphs">
                              {{ version.cfgDFormParagraphs?.length || 0 }} paragraphs
                            </td>
                            @if (!isSelectingActiveVersion) {
                              <td class="version-actions" (click)="$event.stopPropagation()">
                                <button mat-icon-button
                                  matTooltip="Edit this version"
                                  (click)="editVersion(version)">
                                  <lucide-icon name="pencil" [size]="20"></lucide-icon>
                                </button>
                                <button mat-icon-button
                                  matTooltip="View (read-only)"
                                  (click)="viewVersion(version)">
                                  <lucide-icon name="eye" [size]="20"></lucide-icon>
                                </button>
                                <button mat-icon-button
                                  matTooltip="Duplicate version"
                                  (click)="duplicateVersion(version)">
                                  <lucide-icon name="copy" [size]="20"></lucide-icon>
                                </button>
                                <button mat-icon-button
                                  matTooltip="Delete version"
                                  (click)="deleteVersion(version)">
                                  <lucide-icon name="trash-2" [size]="20"></lucide-icon>
                                </button>
                              </td>
                            } @else {
                              <td class="version-actions">
                                <span class="version-selection-hint"
                                  [class.hint-activate]="getVersionStatusName(version.enumDFormBuildStatusID, version) !== 'Active'"
                                  [class.hint-draft]="getVersionStatusName(version.enumDFormBuildStatusID, version) === 'Active'">
                                  {{ getVersionStatusName(version.enumDFormBuildStatusID, version) === 'Active' ? 'Set as Draft' : 'Set as Active' }}
                                </span>
                              </td>
                            }
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                }
                @if (!selectedCfgForm.cfgDFormVersions?.length && selectedCfgForm?.enumFormDataSourceID !== globals.FORM_DATA_SOURCE_CUSTOM) {
                  <div class="no-versions">
                    <lucide-icon name="info" [size]="20"></lucide-icon>
                    @if (!formIsSaved && !formSaveStatusUnknown) {
                      <p>No versions yet. Save the form properties above, then create a version to start building.</p>
                    }
                    @if (formIsSaved || formSaveStatusUnknown) {
                      <p>No versions yet. Create a new version to start building your form.</p>
                    }
                    <div class="no-versions-actions">
                      <!-- See the note on the New Version button above: deliberately not disabled
                           when unsaved, so the click can explain what to do next. -->
                      <button mat-raised-button color="primary"
                        (click)="createNewVersionForCurrentForm()"
                        [matTooltip]="unsavedFormTooltip">
                        <lucide-icon name="plus" [size]="20"></lucide-icon>
                        Create First Version
                      </button>
                      <button mat-raised-button color="primary"
                        (click)="importPdfAsNewVersion()"
                        [matTooltip]="unsavedFormTooltip || 'Import a PDF or Word document into a new version created for you automatically'">
                        <lucide-icon name="file-up" [size]="20"></lucide-icon>
                        Import Document
                      </button>
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Loading Overlay -->
  @if (isLoading) {
    <div class="loading-overlay">
      <mat-spinner></mat-spinner>
    </div>
  }
